import { useEffect, useState } from 'react'
import { App, Card, Col, Row, Statistic, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

import {
  downloadMaintenanceReportCsv,
  maintenanceReportsApi,
  type ReportFilters,
} from '@/features/maintenance/api'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'
import ReportFilterBar from './components/ReportFilterBar'

const { Text } = Typography

interface Row {
  technician_id: string
  technician_name: string | null
  skill_type: string | null
  skill_level: string | null
  jobs_assigned: number
  jobs_started: number
  jobs_completed: number
  jobs_closed: number
  jobs_pending: number
  reopened_jobs: number
  pm_jobs_completed: number
  breakdown_jobs_completed: number
  avg_response_minutes: number
  avg_repair_minutes: number
  total_repair_hours: number
  completion_pct: number
}

/** Spec §8 — Technician performance scorecard. */
export default function TechnicianPerformanceReportPage() {
  const { message } = App.useApp()
  const { plantId } = usePlantId()
  const [filters, setFilters] = useState<ReportFilters>({
    plant_id: plantId,
    from_date: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    to_date: dayjs().format('YYYY-MM-DD'),
  })
  useEffect(() => {
    if (filters.plant_id !== plantId) setFilters((f) => ({ ...f, plant_id: plantId }))
  }, [plantId, filters.plant_id])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['report-tech-perf', filters],
    enabled: !!filters.plant_id,
    queryFn: () => maintenanceReportsApi.technicianPerformance(filters) as unknown as Promise<Row[]>,
  })
  const rows = data ?? []
  const totalAssigned = rows.reduce((a, r) => a + (r.jobs_assigned ?? 0), 0)
  const totalCompleted = rows.reduce((a, r) => a + (r.jobs_completed ?? 0), 0)
  const totalReopened = rows.reduce((a, r) => a + (r.reopened_jobs ?? 0), 0)

  const [exporting, setExporting] = useState(false)
  const exportCsv = async () => {
    setExporting(true)
    try {
      await downloadMaintenanceReportCsv(
        '/maintenance/reports/technician-performance', filters,
        `technician-performance-${dayjs().format('YYYYMMDD-HHmm')}.csv`,
      )
    } catch (e) { message.error((e as Error).message ?? 'Export failed') }
    finally { setExporting(false) }
  }

  const columns = [
    { title: 'Technician', dataIndex: 'technician_name', key: 'n' },
    { title: 'Skill', dataIndex: 'skill_type', key: 's', width: 120 },
    { title: 'Level', dataIndex: 'skill_level', key: 'sl', width: 110 },
    { title: 'Assigned', dataIndex: 'jobs_assigned', key: 'a', width: 100 },
    { title: 'Started', dataIndex: 'jobs_started', key: 'st', width: 100 },
    { title: 'Completed', dataIndex: 'jobs_completed', key: 'c', width: 100 },
    { title: 'Closed', dataIndex: 'jobs_closed', key: 'cl', width: 90 },
    { title: 'Pending', dataIndex: 'jobs_pending', key: 'p', width: 90 },
    { title: 'PM jobs', dataIndex: 'pm_jobs_completed', key: 'pm', width: 90 },
    { title: 'Breakdown jobs', dataIndex: 'breakdown_jobs_completed', key: 'bd', width: 130 },
    { title: 'Reopened', dataIndex: 'reopened_jobs', key: 'ro', width: 100,
      render: (v: number) => v > 0 ? <Text type="warning" strong>{v}</Text> : v,
    },
    { title: 'Avg response (min)', dataIndex: 'avg_response_minutes', key: 'ar', width: 150 },
    { title: 'Avg repair (min)', dataIndex: 'avg_repair_minutes', key: 'arp', width: 140 },
    { title: 'Total repair (h)', dataIndex: 'total_repair_hours', key: 'tr', width: 130 },
    { title: 'Completion %', dataIndex: 'completion_pct', key: 'cp', width: 130,
      render: (v: number) => (
        <Text strong style={{ color: v < 75 ? '#cf1322' : '#52c41a' }}>
          {v.toFixed(1)}%
        </Text>
      ),
    },
  ]

  return (
    <div>
      <PageHeader title="Technician Performance"
        subtitle="Workload, closure speed, repeat-after-repair pressure per technician." />
      <ReportFilterBar
        filters={filters} onChange={setFilters} onRefresh={() => refetch()}
        onExport={exportCsv} exporting={exporting}
        show={{ dateRange: true, skillType: true }}
      />
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}><Card><Statistic title="Technicians" value={rows.length} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Jobs assigned" value={totalAssigned} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Jobs completed" value={totalCompleted} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Reopens" value={totalReopened} valueStyle={{ color: totalReopened > 0 ? '#fa541c' : undefined }} /></Card></Col>
      </Row>
      <DataTable rowKey="technician_id" columns={columns} dataSource={rows} loading={isLoading} />
    </div>
  )
}
